/**
 * SKE-4686 — the marker on scheduled Instagram posts that fall past the cutover.
 *
 * WHY THIS IS A CONTEXT AND NOT A HOOK THE CARDS CALL.
 *
 * Three unrelated post surfaces carry this marker, and every one of them is a
 * leaf rendered hundreds of times in a month view. A `useQuery` inside the card
 * would open one query observer per card, and — the part that actually bites —
 * `useQuery` THROWS when no `QueryClientProvider` sits above it, which is how
 * `calendar-post-card.test.tsx` and its neighbours render today. So the read
 * happens ONCE, in a provider mounted beside the campaign banner, and the cards
 * read a plain value out of context. A tree without the provider gets the inert
 * default and renders no marker, which is the correct degradation as well as
 * the one that leaves every existing test alone.
 *
 * WHAT IT READS. `useCampaignStatus`, the same status endpoint the page
 * checklist reads, NOT the browser-side `getIGApiPublishingStatusOn` twin that
 * the Manage Accounts chip uses. The two derivations are deliberately different
 * — the browser one cannot see the rollout allow-list or the access token — and
 * this issue's "the marker and the checklist must always agree" requirement
 * only holds if both read the server's answer.
 */
import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useSelector } from "react-redux";

import { getUser } from "../../selectors/commonSelectors";
import { safeParseDateString } from "../../utils/date-helpers";
import { formatCutover } from "./campaign-registers";
import { useCampaignStatus } from "./use-campaign-status";

/**
 * The first week the marker is allowed to appear.
 *
 * Week 1 is an invitation and a red marker on the customer's own content would
 * undercut that register; the campaign says "here is something good" in week 1
 * and "here is a deadline" from week 2.
 */
export const CUTOVER_MARKER_FIRST_WEEK = 2;

export interface CutoverMarkerState {
  /** The campaign is on, at week 2 or later, and a cutover date is configured. */
  isActive: boolean;
  /** ISO 8601 cutover instant, or null when none is configured. */
  cutoverAt: string | null;
  /** The customer's own profile timezone, never the browser's. */
  timeZone: string | null;
  /**
   * True when this post is scheduled past the cutover AND its account is one
   * the checklist still shows as outstanding.
   */
  isPostMarked: (
    accountId: string | null | undefined,
    scheduledAt: unknown
  ) => boolean;
  /** The marker's tooltip, with the cutover date already composed into it. */
  tooltip: string;
}

const INERT: CutoverMarkerState = {
  isActive: false,
  cutoverAt: null,
  timeZone: null,
  isPostMarked: () => false,
  tooltip: "",
};

/**
 * Exported for stories, which need to pin a marker state without a query
 * client, a redux store and a fabricated server response behind them. Nothing
 * in the app should provide this directly: use
 * `InstagramCutoverMarkerProvider`, which is the only thing that reads the
 * status endpoint.
 */
export const CutoverMarkerContext = createContext<CutoverMarkerState>(INERT);

/** Reads the marker state. Inert, never throwing, outside the provider. */
export function useCutoverMarker(): CutoverMarkerState {
  return useContext(CutoverMarkerContext);
}

/**
 * The tooltip, verbatim from SKE-4686 with the date filled in.
 *
 * Exported so the stories and the tests read the shipped sentence rather than
 * their own copy of it.
 */
export function cutoverMarkerTooltip(formattedCutover: string): string {
  return `This account needs updating before ${formattedCutover} or this post will not publish.`;
}

export function InstagramCutoverMarkerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { status, isOn, outstanding } = useCampaignStatus();
  const user = useSelector(getUser);
  const timeZone = user?.timezone ?? null;

  const value = useMemo<CutoverMarkerState>(() => {
    const week = status?.campaign?.week;
    const cutoverAt = status?.campaign?.cutoverAt ?? null;
    const isActive =
      isOn &&
      typeof week === "number" &&
      week >= CUTOVER_MARKER_FIRST_WEEK &&
      Boolean(cutoverAt);

    if (!isActive || !cutoverAt) return INERT;

    const cutoverMs = new Date(cutoverAt).getTime();
    if (Number.isNaN(cutoverMs)) return INERT;

    /*
     * `outstanding` is `rowState !== "updated"`, so it carries `not_covered`
     * rows as well as `needs_updating` ones. That is deliberate: it is exactly
     * the set the page checklist shows as outstanding, and a marker derived
     * from a narrower set would be the disagreement this issue exists to
     * prevent.
     */
    const outstandingIds = new Set(outstanding.map((row) => row.accountId));

    return {
      isActive: true,
      cutoverAt,
      timeZone,
      tooltip: cutoverMarkerTooltip(formatCutover(cutoverAt, timeZone)),
      isPostMarked: (accountId, scheduledAt) => {
        if (!accountId || !outstandingIds.has(String(accountId))) return false;
        if (scheduledAt == null || scheduledAt === "") return false;
        // The app's own parser, so a naive "YYYY-MM-DD HH:mm" `when` is read
        // in the customer's timezone exactly as every other post surface reads
        // it, rather than in the browser's.
        const scheduledMs = safeParseDateString(
          scheduledAt,
          timeZone ?? undefined
        ).getTime();
        if (Number.isNaN(scheduledMs)) return false;
        return scheduledMs > cutoverMs;
      },
    };
  }, [status, isOn, outstanding, timeZone]);

  return (
    <CutoverMarkerContext.Provider value={value}>
      {children}
    </CutoverMarkerContext.Provider>
  );
}

/**
 * The marker itself, with no data of its own.
 *
 * Never a full-card colour change: a month with thirty marked posts still has
 * to read as a calendar. App `red-100` fill, `red-600` hairline, `red-700`
 * glyph — the campaign's single red, following the banner's corrected step
 * rather than the spec's marketing-site ramp. red-700 on red-100 measures
 * 6.03:1.
 */
export function CutoverMarkerBadge({
  tooltip,
  className,
}: {
  tooltip: string;
  className?: string;
}) {
  return (
    <span
      data-testid="cutover-post-marker"
      title={tooltip}
      aria-label={tooltip}
      role="img"
      className={[
        "inline-flex size-4 shrink-0 items-center justify-center rounded-[4px]",
        "bg-red-100 ring-1 ring-inset ring-red-600 text-red-700",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <i
        aria-hidden="true"
        className="fa-solid fa-triangle-exclamation text-[9px] leading-none"
      />
    </span>
  );
}

/**
 * The connected marker every post surface renders.
 *
 * Renders nothing at all unless the campaign is live at week 2 or later, the
 * post's account is still outstanding, and the post is scheduled past the
 * cutover. All three surfaces already hold the account and the schedule, so
 * none of them takes a new fetch.
 */
export function CutoverPostMarker({
  accountId,
  scheduledAt,
  className,
}: {
  accountId: string | null | undefined;
  scheduledAt: unknown;
  className?: string;
}) {
  const { isActive, isPostMarked, tooltip } = useCutoverMarker();
  if (!isActive || !isPostMarked(accountId, scheduledAt)) return null;
  return <CutoverMarkerBadge tooltip={tooltip} className={className} />;
}

/**
 * The same marker for a post addressed to several accounts at once.
 *
 * ONE marker, not one per account. The sentence it carries is about the post
 * ("this post will not publish"), and a row of identical triangles would say
 * nothing the first one does not.
 */
export function CutoverPostMarkerForAccounts({
  accountIds,
  scheduledAt,
  className,
}: {
  accountIds: Array<string | null | undefined>;
  scheduledAt: unknown;
  className?: string;
}) {
  const { isActive, isPostMarked, tooltip } = useCutoverMarker();
  if (!isActive) return null;
  if (!accountIds.some((id) => isPostMarked(id, scheduledAt))) return null;
  return <CutoverMarkerBadge tooltip={tooltip} className={className} />;
}

/**
 * The cutover hairline drawn across the calendar-uplift grid.
 *
 * Anchored to the two renderers that already know their own date — the month
 * cell and the scheduler day header — rather than to Mobiscroll's own
 * schedule-column class. That column is vendor DOM: an upgrade moves it and
 * takes the line with it in silence, and the calendar keeps every such name in
 * one module for exactly that reason.
 */
export function CutoverCalendarRule({
  cutoverAt,
  timeZone,
  variant = "cell",
}: {
  cutoverAt: string | null;
  timeZone: string | null;
  variant?: "cell" | "header";
}) {
  if (!cutoverAt) return null;
  const full = formatCutover(cutoverAt, timeZone);
  const label = shortCutoverLabel(cutoverAt, timeZone);
  if (!label) return null;
  return (
    <span
      data-testid="cutover-calendar-rule"
      aria-label={`Instagram publishing changes on ${full}`}
      className={[
        // z-1, deliberately below the cell's own add button at z-2: a 1px rule
        // must never sit over a control the customer is trying to press.
        "pointer-events-none absolute left-0 right-0 z-[1] flex items-center gap-[4px]",
        variant === "header" ? "bottom-0" : "top-0",
      ].join(" ")}
    >
      <span className="h-px flex-1 bg-red-600/50" />
      <span className="shrink-0 text-[10px] font-semibold uppercase leading-[14px] tracking-[0.04em] text-red-700">
        {label}
      </span>
      <span className="h-px flex-1 bg-red-600/50" />
    </span>
  );
}

/**
 * "30 Oct" — the date on its own, which is all a calendar cell has room for.
 *
 * Locale-driven and zone-aware for the same reason `formatCutover` is: two
 * customers reading the same grid are entitled to the same day.
 */
export function shortCutoverLabel(
  cutoverAt: string | null | undefined,
  timeZone?: string | null
): string {
  if (!cutoverAt) return "";
  const date = new Date(cutoverAt);
  if (Number.isNaN(date.getTime())) return "";
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
  };
  try {
    return new Intl.DateTimeFormat(undefined, {
      ...options,
      ...(timeZone ? { timeZone } : {}),
    }).format(date);
  } catch {
    try {
      return new Intl.DateTimeFormat(undefined, options).format(date);
    } catch {
      return "";
    }
  }
}
